import { useMissionStore } from '@/stores/missionStore';
import { useRocketStore } from '@/stores/rocketStore';
import { useSettingsStore } from '@/stores/settingsStore';

export type SaveTone = 'off' | 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface SaveIndicator {
  label: string;
  tone: SaveTone;
}

const TONE_BY_SAVE_STATE: Record<'idle' | 'dirty' | 'saving' | 'saved' | 'error', SaveTone> = {
  idle: 'idle',
  dirty: 'pending',
  saving: 'saving',
  saved: 'saved',
  error: 'error',
};

const PRIORITY: Record<SaveTone, number> = { error: 5, saving: 4, pending: 3, saved: 2, idle: 1, off: 0 };

const LABEL: Record<SaveTone, string> = {
  off: 'Autosave off',
  idle: 'Autosave on',
  pending: 'Unsaved changes',
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Save failed',
};

/** Combines the mission and rocket document stores' save states into one status-bar indicator. */
export function useSaveIndicator(): SaveIndicator {
  const autosaveEnabled = useSettingsStore((state) => state.autosaveEnabled);
  const missionSaveState = useMissionStore((state) => state.saveState);
  const rocketSaveState = useRocketStore((state) => state.saveState);

  if (!autosaveEnabled) return { label: LABEL.off, tone: 'off' };

  const tones = [TONE_BY_SAVE_STATE[missionSaveState], TONE_BY_SAVE_STATE[rocketSaveState]];
  const winner = tones.reduce((worst, current) => (PRIORITY[current] > PRIORITY[worst] ? current : worst), 'idle' as SaveTone);

  return { label: LABEL[winner], tone: winner };
}
